/**
 * Build user-facing ingest completion / failure messages from progress API payload.
 */

import { collectFileErrorHints, shouldShowLegacyStorageKeyHint } from './ingestionFileUploadError';

function appendLinkErrorLines(text, linkErrors, max = 20) {
  if (!Array.isArray(linkErrors) || linkErrors.length === 0) return text;
  let out = `${text}\n\n⚠️ ${linkErrors.length} link error(s) (rows skipped):`;
  for (const e of linkErrors.slice(0, max)) {
    out += `\n  • Row ${e.sheetRow}: [${e.slug}] ${e.errorType} — ${e.message}`;
  }
  if (linkErrors.length > max) {
    out += `\n  … and ${linkErrors.length - max} more`;
  }
  return out;
}

function formatFileErrorLine(entry) {
  const label = entry?.name || entry?.fileId || 'file';
  let msg = entry?.error || entry?.message || 'Upload failed';
  if (entry?.errorCode && !String(msg).includes(`[${entry.errorCode}]`)) {
    msg = `${msg} [${entry.errorCode}]`;
  }
  return `  • ${label}: ${msg}`;
}

function appendFileErrorLines(text, errors, max = 20) {
  if (!Array.isArray(errors) || errors.length === 0) return { text, hasFileErrors: false };
  const countLabel = errors.length === 1 ? '1 file could not be stored' : `${errors.length} files could not be stored`;
  let out = `${text}\n\n⚠️ ${countLabel}:`;
  for (const e of errors.slice(0, max)) {
    out += `\n${formatFileErrorLine(e)}`;
  }
  if (errors.length > max) {
    out += `\n  … and ${errors.length - max} more`;
  }

  const hints = collectFileErrorHints(errors);
  for (const hint of hints) {
    out += `\n\n${hint}`;
  }

  if (shouldShowLegacyStorageKeyHint(errors)) {
    out += '\n\nStorage tip: set SUPABASE_SERVICE_ROLE_KEY on the node server and ensure bucket files_<horizon> exists in Supabase Storage.';
  }

  out += '\n\nDebug: for browser/API failures check the network tab — HTTP 413 = body too large; 502/504 = proxy timeout.';

  return { text: out, hasFileErrors: true };
}

/**
 * @param {object|null|undefined} progress - GET .../ingest/{jobId}/progress data
 * @param {string} groupSlug
 * @returns {{ type: 'success'|'warning'|'error', title: string, text: string }}
 */
export function buildIngestionResultMessage(progress, groupSlug) {
  const slug = groupSlug || 'group';
  const p = progress || {};

  if (p.status === 'error') {
    let text = (p?.error && (p.error.message || p.error)) || p?.failure?.message || 'Ingestion failed';
    if (p?.failure?.hint) {
      text += `\n\n${p.failure.hint}`;
    }
    text = appendLinkErrorLines(text, p?.details?.linkErrors);
    const fileBlock = appendFileErrorLines(text, p?.details?.errors);
    return {
      type: 'error',
      title: 'Ingestion failed',
      text: fileBlock.text,
    };
  }

  const processed = p?.processed ?? 0;
  const total = p?.total ?? processed;
  let text = `Processed ${processed} of ${total} items for '${slug}'.`;
  let type = 'success';

  const fileBlock = appendFileErrorLines(text, p?.details?.errors);
  text = fileBlock.text;
  if (fileBlock.hasFileErrors) type = 'warning';

  text = appendLinkErrorLines(text, p?.details?.linkErrors);

  if (typeof p?.details?.failed === 'number' && p.details.failed > 0) {
    text += `\n\n${p.details.failed} row(s) failed to create.`;
    type = 'warning';
  }

  if (p?.completedWithErrors && p?.summary?.headline && !fileBlock.hasFileErrors) {
    text += `\n\n${p.summary.headline}`;
    if (Array.isArray(p.summary.samples) && p.summary.samples.length) {
      for (const sample of p.summary.samples.slice(0, 5)) {
        text += `\n  • ${sample}`;
      }
    }
    type = 'warning';
  }

  const title = type === 'warning' ? 'Ingestion complete with errors' : 'Ingestion complete';
  return { type, title, text };
}
